import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { SOLUTIONS, INDUSTRIES } from "@/data/content";
import { SOLUTION_DETAILS } from "@/data/solutions";
import { INDUSTRY_DETAILS } from "@/data/industries";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, Stagger, Item } from "@/components/shared/Reveal";
import { Icon } from "@/components/shared/Icon";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function SolutionDetail() {
  const { slug } = useParams();
  const s = SOLUTIONS.find((x) => x.slug === slug);
  const d = SOLUTION_DETAILS[slug];
  useSEO({
    title: s ? `${s.title} | Wexoz Dubai, UAE` : "Solution | Wexoz",
    description: s?.tagline,
    image: s?.image,
    jsonLd: s ? { "@context": "https://schema.org", "@type": "Service", name: s.title, description: s.tagline, serviceType: s.title, areaServed: "United Arab Emirates", provider: { "@id": "https://wexoz.com/#organization" }, url: `https://wexoz.com/solutions/${s.slug}` } : undefined,
  });
  if (!s || !d) return <Navigate to={INDUSTRY_DETAILS[slug] ? `/industries#${slug}` : "/solutions"} replace />;

  const contactHref = `/contact?solution=${encodeURIComponent(d.formValue)}`;
  const others = SOLUTIONS.filter((x) => x.slug !== slug).slice(0, 3);

  return (
    <>
      <PageHero eyebrow={`Solutions · ${d.formValue}`} lines={s.title.split(" & ").length > 1 ? s.title.split(" & ").map((t, i) => (i ? `& ${t}` : t)) : [s.title]} image={d.hero} text={s.tagline}>
        <div className="flex flex-wrap gap-4">
          <Link to={contactHref} data-testid="solution-hero-enquire-cta" className="btn-primary">Enquire about {d.formValue} <ArrowUpRight className="h-4 w-4" /></Link>
          <a href="#capabilities" data-testid="solution-hero-capabilities-link" className="btn-ghost">See capabilities <ArrowRight className="h-4 w-4" /></a>
        </div>
      </PageHero>

      <section className="section">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7 space-y-6 text-base leading-relaxed text-slate-300 md:text-lg">
            <div className="eyebrow">Overview</div>
            {d.intro.map((p, i) => <p key={i}>{p}</p>)}
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="card p-7">
              <div className="eyebrow">How data flows</div>
              <ol className="mt-5 space-y-3">
                {d.flow.map((f, i) => (
                  <li key={f} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line font-mono text-[10px] text-cyan-400">{i + 1}</span>
                    <span className="text-sm font-medium text-white">{f}</span>
                    {i < d.flow.length - 1 && <span className="ml-auto h-px flex-1 max-w-[40px] bg-line-strong" />}
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="capabilities" className="section bg-navy-900/50">
        <div className="wrap">
          <SectionHeading eyebrow="What we deliver" title={`Everything ${d.formValue.toLowerCase()} needs to run as one system.`} />
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {d.groups.map((g, i) => (
              <Item key={g.title} data-testid={`capability-group-${i + 1}`} className="bg-navy p-7">
                <span className="font-mono text-xs text-slate-500">0{i + 1}</span>
                <h3 className="mt-4 text-lg font-semibold">{g.title}</h3>
                <ul className="mt-5 space-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-sm text-slate-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />{it}</li>
                  ))}
                </ul>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="section bg-[#F4F6FA] text-navy">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading light eyebrow="Solution components" title="Hardware, software, connectivity and integrations — specified together." text="Components are selected against your requirement. Established platforms are options, not defaults." />
          </div>
          <Stagger className="lg:col-span-8 grid gap-5 sm:grid-cols-2">
            {Object.entries(d.components).map(([k, items]) => (
              <Item key={k} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-600">{k}</div>
                <ul className="mt-4 space-y-2">
                  {items.map((it) => <li key={it} className="flex items-start gap-2 text-sm text-slate-700"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-navy" />{it}</li>)}
                </ul>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="section">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeading eyebrow="How the engagement works" title="From requirement to running operation." />
            <ol className="mt-10 space-y-0 border-l border-line">
              {d.engagement.map((e, i) => (
                <Reveal key={e} delay={i * 0.05} className="relative pl-8 pb-8 last:pb-0">
                  <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-cyan-400 glow-dot" />
                  <div className="font-mono text-xs text-slate-500">Step 0{i + 1}</div>
                  <div className="mt-1 font-heading text-lg font-medium text-white">{e}</div>
                </Reveal>
              ))}
            </ol>
          </div>
          <div className="lg:col-span-6">
            <SectionHeading eyebrow="Relevant industries" title="Where this solution is typically deployed." />
            <Stagger className="mt-10 grid gap-3 sm:grid-cols-2">
              {d.industries.map((slug) => {
                const ind = INDUSTRIES.find((x) => x.slug === slug);
                return (
                  <Item key={slug}>
                    <Link to={`/industries#${slug}`} data-testid={`solution-industry-${slug}`} className="card flex items-center gap-3 p-4 text-sm font-medium text-white">
                      <Icon name={ind.icon} className="h-4 w-4 text-cyan-400" /> {ind.name}
                    </Link>
                  </Item>
                );
              })}
            </Stagger>
            {d.faqs?.length > 0 && (
              <div className="mt-14">
                <div className="eyebrow">Common questions</div>
                <Accordion type="single" collapsible className="mt-4">
                  {d.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`f${i}`} className="border-line">
                      <AccordionTrigger data-testid={`faq-trigger-${i + 1}`} className="text-left font-heading text-base text-white hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-slate-400">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        </div>
      </section>

      <section data-testid="solution-cta-section" className="relative overflow-hidden bg-cyan-400 py-24 text-navy">
        <div className="wrap flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-navy/70">Start a conversation</div>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold text-navy sm:text-4xl">Discuss {d.formValue.toLowerCase()} for your operation.</h2>
            <p className="mt-3 max-w-xl text-navy/80">Your enquiry will be routed with “{d.formValue}” pre-selected so the right specialist picks it up.</p>
          </div>
          <Link to={contactHref} data-testid="solution-bottom-enquire-cta" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">Talk to an Expert <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <SectionHeading eyebrow="Other solutions" title="Explore related practices." />
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/solutions/${o.slug}`} data-testid={`other-solution-${o.slug}`} className="card group p-6">
                <Icon name={o.icon} className="h-5 w-5 text-cyan-400" />
                <h3 className="mt-5 text-lg font-semibold">{o.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{o.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cyan-400">{o.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
